import {
  checkDeleted,
  checkListDeleted,
  displayNameDescriptor,
  nameDescriptor,
} from 'pages/AutomationEngine/helpers';
import {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { CodeStepDetails, CodeInput, CodeOutput } from './types';
import { INPUT_TYPES, OUTPUT_TYPES } from './constants';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import { CodeStep, PluginCodeStep } from '../actionSectionTypes';

type NameAndLabelLike = { name: string; label: string; required: boolean };
type EntityLookup = { label: string; required: boolean };

function arraysEqualEvery<T extends CodeInput | CodeOutput>(
  targetArr: T[],
  currentArr: T[]
) {
  if (targetArr.length !== currentArr.length) {
    return false;
  }
  return targetArr.every(
    (value, index) => value.name === currentArr[index].name
  );
}

function fixupEntries<T extends CodeInput | CodeOutput>(
  expectedEntries: T[],
  entries: T[]
): [T[], boolean] {
  let nextEntries = entries;
  let isFixedUp = false;

  if (!arraysEqualEvery<T>(expectedEntries, entries)) {
    const currentInputLookup: Record<string, T> = entries.reduce(
      (acc, input) => ({ ...acc, [input.name]: input }),
      {}
    );
    nextEntries = expectedEntries.map((input) => {
      // use the existing then it's not fixed up
      if (currentInputLookup[input.name]) {
        return currentInputLookup[input.name];
      }
      // if it's required flag it as need fix up
      if (input.required) {
        isFixedUp = true;
      }

      return input;
    }) as T[];
  }
  return [nextEntries as T[], isFixedUp];
}

const forFlow = (
  _apiObject: CodeStepDetails & {
    pluginAppApiName: string;
    actionStepApiName: string | null;
    notifyPluginDeveloperOnFailure: boolean;
  },
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary,
  stepType: 'code_step' | 'plugin_code_step',
  automationPluginKeys: string[],
  automationPlugins: any[]
) => {
  let apiObject = _apiObject;
  if (stepType === 'plugin_code_step') {
    const { pluginAppApiName, actionStepApiName, inputs, outputs } = apiObject;

    if (!automationPluginKeys.includes(pluginAppApiName)) {
      addErrorMessage(messageDictionary.missingPlugin);
    } else {
      // fix up inputs and outputs if they have changed since saving
      const automationStep = automationPlugins.find(
        ({ plugin_api_name }) => plugin_api_name === apiObject.pluginAppApiName
      );
      const action = automationStep?.actions[actionStepApiName!];
      if (!action) {
        addErrorMessage(messageDictionary.missingPluginConfig);
        apiObject = {
          ...apiObject,
          actionStepApiName: null,
          inputs: [],
          outputs: [],
        };
      } else {
        const inputLookup: Record<string, EntityLookup> = action.inputs.reduce(
          (acc: any, entry: NameAndLabelLike) => ({
            ...acc,
            [entry.name]: { label: entry.label, required: entry.required },
          }),
          {} as Record<string, string>
        );
        const outputLookup: Record<string, EntityLookup> =
          action.outputs.reduce(
            (acc: any, entry: NameAndLabelLike) => ({
              ...acc,
              [entry.name]: { label: entry.label, required: entry.required },
            }),
            {} as Record<string, EntityLookup>
          );

        const [_nextInputs, isXFixedUp] = fixupEntries<CodeInput>(
          action.inputs,
          inputs
        );
        const nextInputs = _nextInputs.map((input) => ({
          ...input,
          ...inputLookup[input.name],
        }));

        const [_nextOutputs, isYFixedUp] = fixupEntries<CodeOutput>(
          action.outputs,
          outputs
        );
        const nextOutputs = _nextOutputs.map((output) => ({
          ...output,
          ...outputLookup[output.name],
        }));

        if (isXFixedUp || isYFixedUp) {
          addErrorMessage(messageDictionary.updatedPluginConfig);
        }
        apiObject = {
          ...apiObject,
          inputs: nextInputs,
          outputs: nextOutputs,
        };
      }
    }
  }

  checkListDeleted(
    messageDictionary,
    addErrorMessage,
    apiObject.secrets,
    nameDescriptor
  );

  apiObject.inputs?.forEach((input) => {
    if (
      input.inputType === INPUT_TYPES.field ||
      input.inputType === INPUT_TYPES.related_field
    ) {
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        input.field,
        displayNameDescriptor
      );
    }
    if (input.inputType === INPUT_TYPES.related_field) {
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        input.relatedField,
        displayNameDescriptor
      );
    }
    if (input.inputType === INPUT_TYPES.variable) {
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        input.variable,
        displayNameDescriptor
      );
    }
    if (input.inputType === INPUT_TYPES.static_value) {
      if (input.staticValue?.employee) {
        checkDeleted(
          messageDictionary,
          addErrorMessage,
          input.staticValue.employee,
          displayNameDescriptor
        );
      }
      if (input.staticValue?.customObject) {
        checkDeleted(
          messageDictionary,
          addErrorMessage,
          input.staticValue.customObject,
          ({ objectName }: { objectName: string }) => objectName
        );
      }
      if (input.staticValue?.entityRecord) {
        checkDeleted(
          messageDictionary,
          addErrorMessage,
          input.staticValue.entityRecord,
          displayNameDescriptor
        );
      }
    }
  });

  apiObject.outputs?.forEach((output) => {
    if (
      output.outputType === OUTPUT_TYPES.field ||
      output.outputType === OUTPUT_TYPES.related_field
    ) {
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        output.field,
        displayNameDescriptor
      );
    }
    if (output.outputType === OUTPUT_TYPES.related_field) {
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        output.relatedField,
        displayNameDescriptor
      );
    }
  });
  return { ...apiObject, type: stepType };
};

const forApi = (
  step: CodeStep | PluginCodeStep,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  let api = step;

  // the api doesn't allow invalid inputs, so we filter them out
  if (step.type === 'plugin_code_step') {
    const { details } = step;
    const outputs = details.outputs.filter((output) => {
      let valid = true;
      switch (output.outputType) {
        case OUTPUT_TYPES.field:
          valid =
            !!output.name &&
            !!output.field &&
            !!output.conflictResolution &&
            !output.field.deleted;
          break;
        case OUTPUT_TYPES.related_field:
          valid =
            !!output.name &&
            !!output.field &&
            !!output.relatedField &&
            !!output.conflictResolution &&
            !output.field.deleted &&
            !output.relatedField.deleted;
          break;
        case OUTPUT_TYPES.variable:
          valid =
            !!output.name &&
            !!output.variable &&
            !!output.conflictResolution &&
            !output.variable.deleted;
          break;
        default:
          valid = false;
      }
      return valid;
    });
    api = { ...api, details: { ...details, outputs } };
  }

  return {
    ...api,
    details: {
      ...api.details,
      inputs: api.details.inputs.map((input) => {
        if (input.inputType === INPUT_TYPES.static_value) {
          if (input.dataType === AUTOMATION_VARIABLE_DATA_TYPES.employee) {
            return {
              ...input,
              staticValue: {
                ...input.staticValue,
                employeeId: input.staticValue?.employee?.id,
              },
            };
          }
          if (input.dataType === AUTOMATION_VARIABLE_DATA_TYPES.entity) {
            return {
              ...input,
              staticValue: {
                ...input.staticValue,
                entityRecordId: input.staticValue?.entityRecord?.id,
              },
            };
          }
        }

        return input;
      }),
    },
  };
};

export const codeStepDTO = { forApi, forFlow };
